/**
 * ExamMode.jsx — self-evaluated MCQ test.
 * - Draws 8 random questions from a 30-question reserve each attempt.
 * - All state (drawn set, answers, submitted) lives in the STORE, so
 *   visiting the Physics Lab and coming back never resets the test.
 * - Every wrong answer has a "Test it in the Lab" button that drives
 *   the wrong value on a circular track — MCQ failure = game failure.
 */
import { useEffect } from "react";
import { useGame } from "./store";
import { drawExamSet, questionById, wrongSpeedFor } from "./examBank";

export default function ExamMode() {
  const closeExam = useGame((s) => s.closeExam);
  const openLab = useGame((s) => s.openLab);
  const unlockChallenge = useGame((s) => s.unlockChallenge);
  const examIds = useGame((s) => s.examIds);
  const answers = useGame((s) => s.examAnswers);
  const submitted = useGame((s) => s.examSubmitted);
  const setExamIds = useGame((s) => s.setExamIds);
  const setExamAnswer = useGame((s) => s.setExamAnswer);
  const submitExamState = useGame((s) => s.submitExamState);
  const resetExam = useGame((s) => s.resetExam);

  const markProgress = useGame((s) => s.markProgress);

  // Draw a fresh random set only when none exists (first open / after retake)
  useEffect(() => {
    if (examIds.length === 0) setExamIds(drawExamSet(8));
  }, [examIds.length, setExamIds]);

  const questions = examIds.map(questionById).filter(Boolean);
  if (questions.length === 0) return null;

  const allAnswered = questions.every((q) => answers[q.id] != null);
  const score = questions.reduce(
    (acc, q) => acc + (q.options[answers[q.id]]?.correct ? 1 : 0),
    0
  );

  const submit = () => {
    submitExamState();
    unlockChallenge();
    // Passing (≥ 6/8) certifies this phase's exam stamp
    if (score >= 6) markProgress("p1Exam");
    try {
      const best = parseInt(localStorage.getItem("pg_exam_best") || "0", 10);
      if (score > best) localStorage.setItem("pg_exam_best", String(score));
    } catch {}
  };

  const retake = () => resetExam(); // clears ids → new random draw next render

  const testInLab = (q, chosenOpt) => {
    openLab({
      fromExam: true,
      vertical: !!q.lab.vertical,
      r: q.lab.r,
      mu: q.lab.mu ?? 0.5,
      vWrong: wrongSpeedFor(q, chosenOpt.text),
      vCorrect: q.lab.vCorrect,
      question: q.question,
      whyText: chosenOpt.why,
    });
  };

  return (
    <div
      data-testid="exam-mode"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,4,12,0.97)",
        zIndex: 45,
        overflowY: "auto",
        color: "#fff",
        fontFamily: "'Inter', system-ui, sans-serif",
        padding: "30px 16px 60px",
      }}
    >
      <div style={{ width: "min(820px, 100%)", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 4, color: "#e879f9", fontWeight: 700 }}>📝 EXAM MODE</div>
            <h1 style={{ margin: "4px 0", fontSize: 30, fontWeight: 900 }}>
              Circular Motion — Stage Test
            </h1>
          </div>
          <button
            onClick={closeExam}
            style={{ padding: "10px 18px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer" }}
          >
            ← Back to game
          </button>
        </div>
        <p style={{ color: "#9aa4b2", fontSize: 14, marginBottom: 22 }}>
          {submitted
            ? "Review below. For every wrong answer: read why, then TEST IT in the Lab — your test stays exactly as you left it when you come back."
            : `${questions.length} questions drawn at random from a bank of 30 — a different test every attempt. Select an answer for each, then submit.`}
        </p>

        {/* Score banner */}
        {submitted && (
          <div
            data-testid="exam-score"
            style={{
              padding: "16px 20px",
              borderRadius: 14,
              marginBottom: 24,
              textAlign: "center",
              background: score >= 6 ? "rgba(0,255,170,0.12)" : "rgba(255,210,63,0.1)",
              border: `2px solid ${score >= 6 ? "rgba(0,255,170,0.5)" : "rgba(255,210,63,0.45)"}`,
            }}
          >
            <div style={{ fontSize: 34, fontWeight: 900, color: score >= 6 ? "#00ffaa" : "#ffd23f" }}>
              {score} / {questions.length}
            </div>
            <div style={{ fontSize: 14, color: "#cfd6e3", marginTop: 4 }}>
              {score === questions.length
                ? "Perfect score — Challenge mode is yours. 🏁"
                : score >= 6
                  ? "Strong! Test your misses in the Lab (one by one — the test waits for you), then try Challenge mode."
                  : "Each miss has a Lab experiment attached — run them all, watch the physics, then retake for a NEW set of questions."}
            </div>
          </div>
        )}

        {/* Questions */}
        {questions.map((q, qi) => {
          const chosen = answers[q.id];
          const chosenOpt = chosen != null ? q.options[chosen] : null;
          const isWrong = submitted && chosenOpt && !chosenOpt.correct;
          const isRight = submitted && chosenOpt && chosenOpt.correct;
          return (
            <div
              key={q.id}
              style={{
                background: "rgba(10,12,24,0.8)",
                border: `1px solid ${isRight ? "rgba(0,255,170,0.4)" : isWrong ? "rgba(255,77,109,0.4)" : "rgba(255,255,255,0.12)"}`,
                borderRadius: 14,
                padding: "18px 20px",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 11, color: "#3eb6ff", fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
                Q{qi + 1} · {q.topic} {submitted && (isRight ? " · ✓ CORRECT" : " · ✗ WRONG")}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.5, marginBottom: 12 }}>{q.question}</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {q.options.map((opt, oi) => {
                  const selected = chosen === oi;
                  let bg = selected ? "rgba(62,182,255,0.15)" : "rgba(255,255,255,0.04)";
                  let border = selected ? "1px solid rgba(62,182,255,0.55)" : "1px solid rgba(255,255,255,0.12)";
                  let color = "#e8ecf3";
                  if (submitted) {
                    if (opt.correct) {
                      bg = "rgba(0,255,170,0.12)"; border = "1px solid rgba(0,255,170,0.55)"; color = "#00ffaa";
                    } else if (selected) {
                      bg = "rgba(255,77,109,0.12)"; border = "1px solid rgba(255,77,109,0.5)"; color = "#ff8a9b";
                    } else {
                      color = "#7a8294";
                    }
                  }
                  return (
                    <button
                      key={oi}
                      onClick={() => !submitted && setExamAnswer(q.id, oi)}
                      disabled={submitted}
                      style={{
                        textAlign: "left", padding: "11px 14px", borderRadius: 10,
                        fontSize: 13.5, lineHeight: 1.5, fontFamily: "inherit",
                        cursor: submitted ? "default" : "pointer",
                        background: bg, border, color, transition: "all 0.15s",
                      }}
                    >
                      {String.fromCharCode(65 + oi)}. {opt.text}
                    </button>
                  );
                })}
              </div>

              {/* Post-submit feedback */}
              {submitted && chosenOpt && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      padding: "10px 14px", borderRadius: 10, fontSize: 13, lineHeight: 1.6,
                      background: isRight ? "rgba(0,255,170,0.08)" : "rgba(255,77,109,0.08)",
                      border: `1px solid ${isRight ? "rgba(0,255,170,0.3)" : "rgba(255,77,109,0.3)"}`,
                      color: isRight ? "#9fe8c9" : "#ffd0d8",
                    }}
                  >
                    {isRight ? "✓ " : "✗ "}
                    {chosenOpt.why}
                  </div>
                  {isWrong && q.lab && (
                    <button
                      data-testid={`lab-btn-${q.id}`}
                      onClick={() => testInLab(q, chosenOpt)}
                      style={{
                        marginTop: 10, padding: "12px 18px", width: "100%",
                        background: "linear-gradient(135deg, #3eb6ff, #e879f9)",
                        border: "none", borderRadius: 10, color: "#0a0c1e",
                        fontWeight: 800, fontSize: 14, cursor: "pointer",
                      }}
                    >
                      🔬 Don't believe it? TEST IT IN THE LAB — your test will wait here
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Submit / retake */}
        {!submitted ? (
          <button
            data-testid="exam-submit"
            onClick={submit}
            disabled={!allAnswered}
            style={{
              width: "100%", padding: "16px", borderRadius: 12, border: "none",
              background: allAnswered ? "linear-gradient(135deg, #00ffaa, #00b8a9)" : "rgba(255,255,255,0.08)",
              color: allAnswered ? "#001a14" : "#5a6478",
              fontSize: 17, fontWeight: 900, cursor: allAnswered ? "pointer" : "not-allowed", letterSpacing: 1,
            }}
          >
            {allAnswered ? "SUBMIT & SCORE" : `Answer all questions (${Object.keys(answers).filter((k) => examIds.includes(k)).length}/${questions.length})`}
          </button>
        ) : (
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={retake}
              style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" }}
            >
              ↻ Retake — new random questions
            </button>
            <button
              onClick={closeExam}
              style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #ffd23f, #ff8a3d)", color: "#1a0d00", fontWeight: 800, fontSize: 15, cursor: "pointer" }}
            >
              🏁 Done — back to the game
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
